import { useState, useEffect, useRef, useCallback } from "react";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { toast } from "sonner";
import axios from "axios";
import { startRegistration } from "@simplewebauthn/browser";
import {
  User, Shield, Translate, 
  CloudArrowDown, FilePdf, Trash, Eye, EyeSlash, X,
  CaretRight, FloppyDisk, MapPin, Envelope, Phone, Storefront, SignOut, Robot,
  Percent, PencilSimple, Plus, Bell, Check, ChartLineUp, Upload, FileText, FileXls,
  Key, Fingerprint, Laptop, Database, Truck, BookOpen, CloudArrowUp, DotsSixVertical,
  Tag, Warning, HashStraight
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { getApiErrorMessage } from "@/lib/apiError";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/PageHeader";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import { SortableCatalogCard } from "@/components/SortableCatalogCard";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSettings } from "@/contexts/SettingsContext";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import {
  DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, rectSortingStrategy, arrayMove,
} from "@dnd-kit/sortable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Biometric Icon component
function BiometricIcon({ size }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a5 5 0 0 0-5 5v3a5 5 0 0 0 10 0V7a5 5 0 0 0-5-5z"/>
      <path d="M12 14v4"/>
      <path d="M8 18h8"/>
      <circle cx="12" cy="8" r="1"/>
    </svg>
  );
}

// Numeric keypad component
function NumericKeypad({ onInput, onBackspace }) {
  return (
    <div className="grid grid-cols-3 gap-3 max-w-[240px] mx-auto">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, 'back'].map((key, idx) => (
        <button
          key={idx}
          onClick={() => {
            if (key === 'back') onBackspace();
            else if (key !== null) onInput(String(key));
          }}
          disabled={key === null}
          className={`
            h-14 rounded-xl text-xl font-semibold transition-all
            ${key === null ? 'invisible' : ''}
            ${key === 'back' 
              ? 'bg-stone-200 dark:bg-stone-700 text-stone-600 dark:text-stone-300 hover:bg-stone-300' 
              : 'bg-stone-100 dark:bg-stone-800 text-stone-900 dark:text-white hover:bg-[#00A19A]/20 active:bg-[#00A19A]/30'
            }
          `}
          data-testid={`keypad-${key}`}
        >
          {key === 'back' ? '⌫' : key}
        </button>
      ))}
    </div>
  );
}

export default function Profile() {
  const { 
    settings, updateSetting, setPin, removePin, t, languages, clearCache,
    biometricAvailable, enableBiometric, disableBiometric, logout
  } = useSettings();
  const { user: sessionUser, logout: sessionLogout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = sessionUser?.role === 'admin';

  // Admin: user management
  const [adminUsers, setAdminUsers] = useState([]);
  const [loadingAdminUsers, setLoadingAdminUsers] = useState(false);
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ email: '', name: '', password: '', role: 'user' });
  const [savingNewUser, setSavingNewUser] = useState(false);
  const [userActionId, setUserActionId] = useState(null);

  // Admin: storage stats (DB size + disk space, shown in "Armazenamento")
  const [storageStats, setStorageStats] = useState(null);
  const [loadingStorageStats, setLoadingStorageStats] = useState(false);

  const fetchStorageStats = async () => {
    setLoadingStorageStats(true);
    try {
      const res = await axios.get(`${API}/auth/storage-stats`);
      setStorageStats(res.data);
    } catch (error) {
      toast.error("Erro ao carregar dados de armazenamento: " + getApiErrorMessage(error));
    } finally {
      setLoadingStorageStats(false);
    }
  };

  const fetchAdminUsers = async () => {
    setLoadingAdminUsers(true);
    try {
      const res = await axios.get(`${API}/auth/users`);
      setAdminUsers(res.data || []);
    } catch (error) {
      toast.error("Erro ao carregar utilizadores: " + getApiErrorMessage(error));
    } finally {
      setLoadingAdminUsers(false);
    }
  };

  const createAdminUser = async () => {
    if (!newUserForm.email || !newUserForm.name || !newUserForm.password) {
      toast.error("Preencha email, nome e senha");
      return;
    }
    setSavingNewUser(true);
    try {
      await axios.post(`${API}/auth/users`, newUserForm);
      toast.success("Utilizador criado com sucesso");
      setShowAddUserForm(false);
      setNewUserForm({ email: '', name: '', password: '', role: 'user' });
      await fetchAdminUsers();
    } catch (error) {
      toast.error("Erro ao criar: " + getApiErrorMessage(error));
    } finally {
      setSavingNewUser(false);
    }
  };

  const toggleBlockUser = async (targetUser) => {
    const action = targetUser.is_active ? 'block' : 'unblock';
    if (action === 'block' && !window.confirm(`Bloquear o acesso de "${targetUser.name}"? A sessão dele(a) será terminada imediatamente.`)) return;
    setUserActionId(targetUser.id);
    try {
      await axios.patch(`${API}/auth/users/${targetUser.id}/${action}`);
      toast.success(action === 'block' ? "Acesso bloqueado" : "Acesso desbloqueado");
      await fetchAdminUsers();
    } catch (error) {
      toast.error("Erro: " + getApiErrorMessage(error));
    } finally {
      setUserActionId(null);
    }
  };

  const deleteAdminUser = async (targetUser) => {
    if (!window.confirm(`Eliminar definitivamente o utilizador "${targetUser.name}"?`)) return;
    setUserActionId(targetUser.id);
    try {
      await axios.delete(`${API}/auth/users/${targetUser.id}`);
      toast.success("Utilizador eliminado");
      await fetchAdminUsers();
    } catch (error) {
      toast.error("Erro ao eliminar: " + getApiErrorMessage(error));
    } finally {
      setUserActionId(null);
    }
  };

  // Real account password change + Face ID/Touch ID passkeys (unlock the real session)
  const [changePwForm, setChangePwForm] = useState({ current: '', next: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [passkeys, setPasskeys] = useState([]);
  const [loadingPasskeys, setLoadingPasskeys] = useState(false);
  const [registeringPasskey, setRegisteringPasskey] = useState(false);
  const [passkeyPlatformSupported, setPasskeyPlatformSupported] = useState(false);
  const [passkeyActionId, setPasskeyActionId] = useState(null);

  useEffect(() => {
    if (window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable) {
      window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        .then(setPasskeyPlatformSupported)
        .catch(() => setPasskeyPlatformSupported(false));
    }
    fetchPasskeys();
  }, []);

  const fetchPasskeys = async () => {
    setLoadingPasskeys(true);
    try {
      const res = await axios.get(`${API}/auth/passkeys`);
      setPasskeys(res.data || []);
    } catch (error) {
      console.error("Error fetching passkeys:", error);
    } finally {
      setLoadingPasskeys(false);
    }
  };

  const handleChangePassword = async () => {
    if (!changePwForm.current || !changePwForm.next) {
      toast.error("Preencha a senha atual e a nova senha");
      return;
    }
    if (changePwForm.next !== changePwForm.confirm) {
      toast.error("As senhas novas não coincidem");
      return;
    }
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: changePwForm.current,
        new_password: changePwForm.next,
      });
      toast.success("Senha alterada com sucesso");
      setChangePwForm({ current: '', next: '', confirm: '' });
    } catch (error) {
      toast.error("Erro: " + getApiErrorMessage(error));
    } finally {
      setChangingPassword(false);
    }
  };

  const registerPasskey = async () => {
    setRegisteringPasskey(true);
    try {
      const { data: optionsJSON } = await axios.post(`${API}/auth/passkeys/register/options`);
      const credential = await startRegistration({ optionsJSON });
      await axios.post(`${API}/auth/passkeys/register/verify`, credential);
      toast.success("Face ID / Touch ID ativado neste dispositivo");
      await fetchPasskeys();
    } catch (error) {
      if (error?.name === 'NotAllowedError') {
        toast.error("Cancelado, ou este dispositivo não suporta Face ID / Touch ID");
      } else {
        toast.error("Erro: " + getApiErrorMessage(error));
      }
    } finally {
      setRegisteringPasskey(false);
    }
  };

  const removePasskey = async (passkeyId) => {
    if (!window.confirm("Remover este Face ID / Touch ID? Vai deixar de poder entrar com ele neste dispositivo.")) return;
    setPasskeyActionId(passkeyId);
    try {
      await axios.delete(`${API}/auth/passkeys/${passkeyId}`);
      toast.success("Removido");
      await fetchPasskeys();
    } catch (error) {
      toast.error("Erro ao remover: " + getApiErrorMessage(error));
    } finally {
      setPasskeyActionId(null);
    }
  };

  // Profile data
  const [profile, setProfile] = useState({
    nome: '',
    marca: '',
    zona: '',
    email: '',
    contato: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Active section state
  // Pode abrir diretamente numa secção (ex: vindo da página pública de Catálogos
  // que aponta para "Configurações > Gerir Catálogos")
  const location = useLocation();
  const [activeSection, setActiveSection] = useState(location.state?.openSection || null);

  // Rappel Management
  const [rappelTargets, setRappelTargets] = useState([]);
  const [loadingRappelTargets, setLoadingRappelTargets] = useState(false);
  const [uploadingRappelFile, setUploadingRappelFile] = useState(false);
  const rappelFileInputRef = useRef(null);
  const [editingRappelNumber, setEditingRappelNumber] = useState(null);
  const [rappelEditForm, setRappelEditForm] = useState(null);
  const [showRappelAddForm, setShowRappelAddForm] = useState(false);
  const emptyRappelTiers = () => ([{ objetivo: '', percentual: '' }, { objetivo: '', percentual: '' }, { objetivo: '', percentual: '' }]);
  const [rappelNewForm, setRappelNewForm] = useState({ client_number: '', client_name: '', tiers: emptyRappelTiers() });

  // Alert Settings
  const [alertSettings, setAlertSettings] = useState({ visit_frequency_days: 90 });
  const [savingAlertSettings, setSavingAlertSettings] = useState(false);
  const [showClientAlertsDialog, setShowClientAlertsDialog] = useState(false);
  const [clientAlertSettings, setClientAlertSettings] = useState([]);
  const [loadingClientAlertSettings, setLoadingClientAlertSettings] = useState(false);
  const [clientAlertSearch, setClientAlertSearch] = useState('');
  const [savingClientAlertId, setSavingClientAlertId] = useState(null);

  // Vendas (GPP Sales) Data Upload
  const [vendasFiles, setVendasFiles] = useState([]);
  const [parsingVendas, setParsingVendas] = useState(false);
  const [vendasLastUpdated, setVendasLastUpdated] = useState(null);
  const vendasFileInputRef = useRef(null);
  // Objetivo Mensal (€) - usado na "Previsão de Fecho" em Análise de Vendas quando o PDF
  // Monthly Sales não tem "Target END of MONTH" definido.
  const [monthlyGoalInput, setMonthlyGoalInput] = useState('');
  const [savedMonthlyGoal, setSavedMonthlyGoal] = useState(null);
  const [savingMonthlyGoal, setSavingMonthlyGoal] = useState(false);

  // Lista de Preços - Actualização via Excel (Configurações)
  const [priceListMeta, setPriceListMeta] = useState(null);
  const [uploadingPriceList, setUploadingPriceList] = useState(false);
  const [priceListPendingFile, setPriceListPendingFile] = useState(null);
  const [priceListRemovalPreview, setPriceListRemovalPreview] = useState(null);
  const [showPriceListRemovalDialog, setShowPriceListRemovalDialog] = useState(false);
  const priceListFileInputRef = useRef(null);

  const fetchPriceListMeta = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/price-products-meta/last-update`);
      setPriceListMeta(res.data?.last_updated ? res.data : null);
    } catch (error) {
      console.error("Error fetching price list metadata:", error);
    }
  }, []);

  useEffect(() => {
    fetchPriceListMeta();
  }, [fetchPriceListMeta]);

  const submitPriceListImport = async (file, confirmRemoval) => {
    setUploadingPriceList(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(
        `${API}/price-products/import${confirmRemoval ? '?confirm_removal=true' : ''}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data.requires_confirmation) {
        setPriceListPendingFile(file);
        setPriceListRemovalPreview(res.data);
        setShowPriceListRemovalDialog(true);
      } else {
        toast.success(`Lista de preços atualizada: ${res.data.added} adicionado(s), ${res.data.updated} atualizado(s)${res.data.removed ? `, ${res.data.removed} removido(s)` : ''}`);
        setShowPriceListRemovalDialog(false);
        setPriceListPendingFile(null);
        setPriceListRemovalPreview(null);
        await fetchPriceListMeta();
      }
    } catch (error) {
      toast.error("Erro ao importar lista de preços: " + getApiErrorMessage(error));
    } finally {
      setUploadingPriceList(false);
      if (priceListFileInputRef.current) priceListFileInputRef.current.value = '';
    }
  };

  const handlePriceListFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
      toast.error("Por favor selecione um ficheiro Excel (.xlsx)");
      return;
    }
    submitPriceListImport(file, false);
  };

  const confirmPriceListRemoval = () => {
    if (priceListPendingFile) {
      submitPriceListImport(priceListPendingFile, true);
    }
  };

  const cancelPriceListRemoval = () => {
    setShowPriceListRemovalDialog(false);
    setPriceListPendingFile(null);
    setPriceListRemovalPreview(null);
  };

  // Tabelas Especiais de Preços (Outras Escolhas / Volume / Especiais) - import PDF
  const [priceExtrasMeta, setPriceExtrasMeta] = useState({});
  const [uploadingExtra, setUploadingExtra] = useState(null); // chave do tipo a carregar
  const outrasEscolhasFileInputRef = useRef(null);
  const volume2tierFileInputRef = useRef(null);
  const volume1tierFileInputRef = useRef(null);
  const specialCampaignFileInputRef = useRef(null);
  const groupTableFileInputRef = useRef(null);

  const fetchPriceExtrasMeta = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/price-extras/meta`);
      setPriceExtrasMeta(res.data || {});
    } catch (error) {
      console.error("Error fetching price extras metadata:", error);
    }
  }, []);

  useEffect(() => {
    fetchPriceExtrasMeta();
  }, [fetchPriceExtrasMeta]);

  const handlePriceExtraUpload = async (e, endpoint, metaKey) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error("Por favor selecione um ficheiro PDF");
      e.target.value = '';
      return;
    }
    setUploadingExtra(metaKey);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/price-extras/${endpoint}/import`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Tabela atualizada: ${res.data.count} registo(s) importado(s)`);
      await fetchPriceExtrasMeta();
    } catch (error) {
      toast.error("Erro ao importar tabela: " + getApiErrorMessage(error));
    } finally {
      setUploadingExtra(null);
      e.target.value = '';
    }
  };


  useEffect(() => {
    axios.get(`${API}/sales/vendas`).then(res => {
      setVendasLastUpdated(res.data?.last_updated || null);
    }).catch(() => {});
    axios.get(`${API}/sales/monthly-goal`).then(res => {
      const target = res.data?.monthly_target;
      setSavedMonthlyGoal(target ?? null);
      setMonthlyGoalInput(target != null ? String(target) : '');
    }).catch(() => {});
  }, []);

  const handleSaveMonthlyGoal = async () => {
    const value = parseFloat(monthlyGoalInput);
    if (!value || value <= 0) {
      toast.error("Indique um Objetivo Mensal válido (€)");
      return;
    }
    setSavingMonthlyGoal(true);
    try {
      await axios.put(`${API}/sales/monthly-goal`, { monthly_target: value });
      setSavedMonthlyGoal(value);
      toast.success("Objetivo Mensal guardado");
    } catch (error) {
      toast.error("Erro ao guardar Objetivo Mensal: " + getApiErrorMessage(error));
    } finally {
      setSavingMonthlyGoal(false);
    }
  };

  const handleVendasFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const newFiles = [];
    for (const file of files) {
      const reader = new FileReader();
      reader.onload = (event) => {
        newFiles.push({
          name: file.name,
          type: file.type,
          size: file.size,
          content: event.target.result
        });
        if (newFiles.length === files.length) {
          setVendasFiles(prev => [...prev, ...newFiles]);
          toast.success(`${files.length} ficheiro(s) carregado(s)`);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const removeVendasFile = (index) => {
    setVendasFiles(prev => prev.filter((_, i) => i !== index));
  };

  const parseVendasData = async () => {
    if (vendasFiles.length === 0) {
      toast.error("Por favor, carregue ficheiros GPP para análise");
      return;
    }
    setParsingVendas(true);
    try {
      const response = await axios.post(`${API}/sales/vendas/parse`, {
        files: vendasFiles.map(f => ({ name: f.name, content: f.content }))
      });
      setVendasLastUpdated(response.data?.last_updated || null);
      setVendasFiles([]);
      toast.success("Dados de vendas atualizados com sucesso! Consulte a Análise de Vendas.");
      setActiveSection(null);
    } catch (error) {
      console.error("Parse error:", error);
      toast.error("Erro ao processar: " + getApiErrorMessage(error));
    } finally {
      setParsingVendas(false);
    }
  };

  const getVendasFileIcon = (filename) => {
    if (filename.endsWith('.pdf')) return <FilePdf size={24} className="text-red-500" />;
    if (filename.endsWith('.xlsx') || filename.endsWith('.xls')) return <FileXls size={24} className="text-green-600" />;
    return <FileText size={24} className="text-stone-500" />;
  };

  // Encomendas Diárias (GPP daily orders) Data Upload
  const [encomendaFiles, setEncomendaFiles] = useState([]);
  const [parsingEncomendas, setParsingEncomendas] = useState(false);
  const [encomendasLastUpdated, setEncomendasLastUpdated] = useState(null);
  const encomendaFileInputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/encomendas`).then(res => {
      setEncomendasLastUpdated(res.data?.last_updated || null);
    }).catch(() => {});
  }, []);

  const handleEncomendaFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const newFiles = [];
    for (const file of files) {
      const reader = new FileReader();
      reader.onload = (event) => {
        newFiles.push({
          name: file.name,
          type: file.type,
          size: file.size,
          content: event.target.result
        });
        if (newFiles.length === files.length) {
          setEncomendaFiles(prev => [...prev, ...newFiles]);
          toast.success(`${files.length} ficheiro(s) carregado(s)`);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const removeEncomendaFile = (index) => {
    setEncomendaFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadEncomendasData = async () => {
    if (encomendaFiles.length === 0) {
      toast.error("Por favor, carregue os PDFs das encomendas diárias");
      return;
    }
    setParsingEncomendas(true);
    try {
      const response = await axios.post(`${API}/encomendas/upload`, {
        files: encomendaFiles.map(f => ({ name: f.name, content: f.content }))
      });
      setEncomendasLastUpdated(response.data?.last_updated || null);
      setEncomendaFiles([]);
      toast.success(`${response.data?.count || 0} cliente(s) processado(s)! Consulte Encomendas Pendentes.`);
      setActiveSection(null);
    } catch (error) {
      console.error("Parse error:", error);
      toast.error("Erro ao processar: " + getApiErrorMessage(error));
    } finally {
      setParsingEncomendas(false);
    }
  };
  
  // PIN Dialog states
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pinAction, setPinAction] = useState('set');
  const [pinStep, setPinStep] = useState(1);
  const [pinValue, setPinValue] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [currentPin, setCurrentPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [enablingBiometric, setEnablingBiometric] = useState(false);
  
  // Gerir Catálogos (biblioteca de PDFs) - grelha com drag & drop, título por ficheiro
  const [catalogs, setCatalogs] = useState([]);
  const [catalogUploading, setCatalogUploading] = useState(false);
  const [catalogUploadProgress, setCatalogUploadProgress] = useState(null);
  const [pendingCatalogFiles, setPendingCatalogFiles] = useState([]); // [{file, title}] - à espera de confirmação de título
  const [deleteCatalogId, setDeleteCatalogId] = useState(null);
  const fileInputRef = useRef(null);

  const fetchCatalogs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/catalogs`);
      setCatalogs(res.data);
    } catch (error) {
      toast.error("Erro ao carregar catálogos");
    }
  }, []);

  useEffect(() => { fetchCatalogs(); }, [fetchCatalogs]);

  const handleCatalogFilesSelected = (e) => {
    const files = Array.from(e.target.files || []).filter((f) => f.name.toLowerCase().endsWith('.pdf'));
    if (files.length === 0) return;
    setPendingCatalogFiles(files.map((file) => ({ file, title: file.name.replace(/\.pdf$/i, '') })));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const updatePendingCatalogTitle = (index, title) => {
    setPendingCatalogFiles((prev) => prev.map((p, i) => (i === index ? { ...p, title } : p)));
  };

  const removePendingCatalogFile = (index) => {
    setPendingCatalogFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const confirmCatalogUpload = async () => {
    const items = pendingCatalogFiles;
    if (items.length === 0) return;
    setCatalogUploading(true);
    let successCount = 0, failCount = 0;
    for (let i = 0; i < items.length; i++) {
      setCatalogUploadProgress({ current: i + 1, total: items.length });
      try {
        const formData = new FormData();
        formData.append('file', items[i].file);
        formData.append('title', items[i].title || items[i].file.name.replace(/\.pdf$/i, ''));
        await axios.post(`${API}/catalogs/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 180000,
        });
        successCount++;
      } catch (error) {
        failCount++;
      }
    }
    if (successCount > 0) {
      toast.success(successCount === 1 ? "Catálogo carregado com sucesso" : `${successCount} catálogos carregados com sucesso`);
    }
    if (failCount > 0) {
      toast.error(`${failCount} catálogo(s) falharam ao carregar`);
    }
    setPendingCatalogFiles([]);
    setCatalogUploading(false);
    setCatalogUploadProgress(null);
    fetchCatalogs();
  };

  const renameCatalog = async (catalogId, title) => {
    setCatalogs((prev) => prev.map((c) => (c.id === catalogId ? { ...c, title } : c)));
    try {
      await axios.put(`${API}/catalogs/${catalogId}`, { title });
    } catch (error) {
      toast.error("Erro ao atualizar título");
      fetchCatalogs();
    }
  };

  const handleDeleteCatalog = async () => {
    try {
      await axios.delete(`${API}/catalogs/${deleteCatalogId}`);
      toast.success("Catálogo eliminado");
      fetchCatalogs();
    } catch (error) {
      toast.error("Erro ao eliminar catálogo");
    } finally {
      setDeleteCatalogId(null);
    }
  };

  const catalogDragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } })
  );

  const handleCatalogDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = catalogs.findIndex((c) => c.id === active.id);
    const newIndex = catalogs.findIndex((c) => c.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(catalogs, oldIndex, newIndex);
    setCatalogs(reordered);
    try {
      await axios.put(`${API}/catalogs/reorder`, { ordered_ids: reordered.map((c) => c.id) });
    } catch (error) {
      toast.error("Erro ao guardar a nova ordem");
      fetchCatalogs();
    }
  };

  // Chatbot PDF Management
  const [pdfTexts, setPdfTexts] = useState([]);
  const [loadingPdfs, setLoadingPdfs] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const pdfFileInputRef = useRef(null);
  
  // Fetch indexed PDFs for chatbot
  const fetchPdfTexts = async () => {
    setLoadingPdfs(true);
    try {
      const response = await axios.get(`${API}/pdf/extracted-texts`);
      setPdfTexts(response.data.pdfs || []);
    } catch (error) {
      console.error("Error fetching PDF texts:", error);
    } finally {
      setLoadingPdfs(false);
    }
  };
  
  // Upload one PDF/Excel file for chatbot, returns true on success
  const uploadOneChatbotFile = async (file, docName) => {
    const fileName = file.name.toLowerCase();
    const isPdf = fileName.endsWith('.pdf');
    const formData = new FormData();
    formData.append('file', file);
    
    let response;
    if (isPdf) {
      formData.append('pdf_name', docName);
      formData.append('pdf_type', 'custom');
      response = await axios.post(`${API}/pdf/upload-and-extract`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000
      });
    } else {
      formData.append('doc_name', docName);
      formData.append('doc_type', 'price_list');
      response = await axios.post(`${API}/pdf/upload-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000
      });
    }
    return response.data;
  };
  
  // Upload PDF/Excel files for chatbot - supports selecting multiple files at once
  const handleChatbotFileUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    
    const validFiles = files.filter(f => {
      const n = f.name.toLowerCase();
      return n.endsWith('.pdf') || n.endsWith('.xlsx') || n.endsWith('.xls');
    });
    
    if (validFiles.length === 0) {
      toast.error("Por favor selecione ficheiros PDF ou Excel (.xlsx)");
      return;
    }
    
    setUploadingPdf(true);
    let successCount = 0;
    let failCount = 0;
    
    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      const docName = file.name.replace(/\.(pdf|xlsx|xls)$/i, '');
      toast.loading(`A processar ${i + 1}/${validFiles.length}: ${docName}...`, { id: "file-upload" });
      try {
        const data = await uploadOneChatbotFile(file, docName);
        if (data.chars_extracted > 0) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (error) {
        failCount++;
      }
    }
    
    toast.dismiss("file-upload");
    if (successCount > 0) {
      toast.success(`${successCount} documento(s) carregado(s) com sucesso${failCount > 0 ? `, ${failCount} falhou/falharam` : ''}`);
      await fetchPdfTexts();
    } else {
      toast.error("Nenhum ficheiro tinha texto extraível");
    }
    
    setUploadingPdf(false);
    if (pdfFileInputRef.current) {
      pdfFileInputRef.current.value = '';
    }
  };
  
  // Clear all chatbot PDF texts
  const clearPdfTexts = async () => {
    if (!window.confirm("Eliminar todos os documentos indexados do chatbot?")) return;
    try {
      await axios.delete(`${API}/pdf/extracted-texts`);
      setPdfTexts([]);
      toast.success("Documentos eliminados");
    } catch (error) {
      toast.error("Erro ao eliminar");
    }
  };
  
  // Delete a single chatbot PDF text
  const deletePdfText = async (pdfName) => {
    if (!window.confirm(`Eliminar o documento "${pdfName}"?`)) return;
    try {
      await axios.delete(`${API}/pdf/extracted-texts/${encodeURIComponent(pdfName)}`);
      setPdfTexts((prev) => prev.filter((p) => p.pdf_name !== pdfName));
      toast.success(`"${pdfName}" eliminado`);
    } catch (error) {
      toast.error("Erro ao eliminar documento");
    }
  };

  // Load profile on mount
  useEffect(() => {
    const savedProfile = localStorage.getItem('gresart_profile');
    if (savedProfile) {
      try {
        setProfile(JSON.parse(savedProfile));
      } catch (e) {
        console.error('Error loading profile:', e);
      }
    }
    // Also fetch the indexed PDF count right away, so the "Chatbot IA" card shows the
    // correct number immediately instead of "0 documentos indexados" until the user
    // opens that sub-section.
    fetchPdfTexts();
  }, []);
  
  // Re-fetch when the chatbot section itself is opened (keeps the list fresh after uploads/deletes)
  useEffect(() => {
    if (activeSection === 'chatbot') {
      fetchPdfTexts();
    }
    if (activeSection === 'users' && isAdmin) {
      fetchAdminUsers();
    }
    if (activeSection === 'storage' && isAdmin) {
      fetchStorageStats();
    }
  }, [activeSection]);

  // Save profile
  const saveProfile = () => {
    setSavingProfile(true);
    try {
      localStorage.setItem('gresart_profile', JSON.stringify(profile));
      toast.success('Perfil guardado');
    } catch (e) {
      toast.error('Erro ao guardar perfil');
    } finally {
      setSavingProfile(false);
    }
  };

  // Biometric handlers
  const handleEnableBiometric = async () => {
    setEnablingBiometric(true);
    try {
      const success = await enableBiometric();
      if (success) {
        toast.success('Face ID / Touch ID ativado');
      } else {
        toast.error('Não foi possível ativar');
      }
    } catch (error) {
      toast.error('Erro ao ativar biometria');
    } finally {
      setEnablingBiometric(false);
    }
  };

  const handleDisableBiometric = () => {
    disableBiometric();
    toast.success('Face ID / Touch ID desativado');
  };

  // Logout handler
  const handleLogout = async () => {
    logout(); // legacy read-only/PIN mode toggle (unrelated feature, kept as-is)
    await sessionLogout(); // real session logout - required now that login is mandatory
    toast.success("Sessão terminada");
    navigate("/login", { replace: true });
  };

  // PIN Dialog handlers
  const openPinDialog = (action) => {
    setPinAction(action);
    setPinStep(action === 'set' ? 1 : 1);
    setPinValue('');
    setPinConfirm('');
    setCurrentPin('');
    setPinError('');
    setShowPin(false);
    setShowPinDialog(true);
  };

  const handlePinInput = (digit) => {
    if (pinAction === 'change' || pinAction === 'remove') {
      if (pinStep === 1 && currentPin.length < 4) {
        setCurrentPin(prev => prev + digit);
        setPinError('');
      } else if (pinStep === 2 && pinValue.length < 4) {
        setPinValue(prev => prev + digit);
        setPinError('');
      } else if (pinStep === 3 && pinConfirm.length < 4) {
        setPinConfirm(prev => prev + digit);
        setPinError('');
      }
    } else {
      if (pinStep === 1 && pinValue.length < 4) {
        setPinValue(prev => prev + digit);
        setPinError('');
      } else if (pinStep === 2 && pinConfirm.length < 4) {
        setPinConfirm(prev => prev + digit);
        setPinError('');
      }
    }
  };

  const handlePinBackspace = () => {
    if (pinAction === 'change' || pinAction === 'remove') {
      if (pinStep === 1) setCurrentPin(prev => prev.slice(0, -1));
      else if (pinStep === 2) setPinValue(prev => prev.slice(0, -1));
      else if (pinStep === 3) setPinConfirm(prev => prev.slice(0, -1));
    } else {
      if (pinStep === 1) setPinValue(prev => prev.slice(0, -1));
      else if (pinStep === 2) setPinConfirm(prev => prev.slice(0, -1));
    }
    setPinError('');
  };

  const handlePinContinue = () => {
    if (pinAction === 'set') {
      if (pinStep === 1) {
        setPinStep(2);
      } else if (pinStep === 2) {
        if (pinValue !== pinConfirm) {
          setPinError(t('settings_pin_mismatch') || 'Os PINs não coincidem');
          setPinConfirm('');
          return;
        }
        setPin(pinValue);
        setShowPinDialog(false);
        toast.success(t('settings_pin_set') || 'PIN definido com sucesso');
      }
    } else if (pinAction === 'change') {
      if (pinStep === 1) {
        if (currentPin !== settings.pin) {
          setPinError(t('settings_pin_incorrect') || 'PIN atual incorreto');
          setCurrentPin('');
          return;
        }
        setPinStep(2);
      } else if (pinStep === 2) {
        setPinStep(3);
      } else if (pinStep === 3) {
        if (pinValue !== pinConfirm) {
          setPinError(t('settings_pin_mismatch') || 'Os PINs não coincidem');
          setPinConfirm('');
          return;
        }
        setPin(pinValue);
        setShowPinDialog(false);
        toast.success(t('settings_pin_changed') || 'PIN alterado com sucesso');
      }
    } else if (pinAction === 'remove') {
      if (currentPin !== settings.pin) {
        setPinError(t('settings_pin_incorrect') || 'PIN incorreto');
        setCurrentPin('');
        return;
      }
      removePin();
      setShowPinDialog(false);
      toast.success(t('settings_pin_removed') || 'PIN removido com sucesso');
    }
  };

  const getCurrentPinDisplay = () => {
    if (pinAction === 'change' || pinAction === 'remove') {
      if (pinStep === 1) return currentPin;
      if (pinStep === 2) return pinValue;
      if (pinStep === 3) return pinConfirm;
    }
    return pinStep === 1 ? pinValue : pinConfirm;
  };

  const getPinDialogTitle = () => {
    if (pinAction === 'remove') return t('settings_enter_pin') || 'Introduza o PIN atual';
    if (pinAction === 'change') {
      if (pinStep === 1) return t('settings_enter_current_pin') || 'Introduza o PIN atual';
      if (pinStep === 2) return t('settings_enter_new_pin') || 'Introduza o novo PIN';
      return t('settings_confirm_new_pin') || 'Confirme o novo PIN';
    }
    return pinStep === 1 
      ? (t('settings_enter_new_pin') || 'Introduza um PIN de 4 dígitos')
      : (t('settings_confirm_pin') || 'Confirme o PIN');
  };

  const handleExportData = async () => {
    try {
      const response = await axios.get(`${API}/export/all`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `gresart_export_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t('settings_export_success') || 'Dados exportados com sucesso');
    } catch (error) {
      toast.error(t('common_error') || 'Erro ao exportar dados');
    }
  };

  const handleClearCache = () => {
    clearCache();
    toast.success(t('settings_cache_cleared') || 'Cache limpo com sucesso');
  };

  // Rappel Management handlers
  const fetchRappelTargets = async () => {
    setLoadingRappelTargets(true);
    try {
      const res = await axios.get(`${API}/rappel/targets`);
      setRappelTargets(res.data || []);
    } catch (error) {
      console.error("Error fetching rappel targets:", error);
    } finally {
      setLoadingRappelTargets(false);
    }
  };

  useEffect(() => {
    fetchRappelTargets();
  }, []);

  useEffect(() => {
    const fetchAlertSettings = async () => {
      try {
        const res = await axios.get(`${API}/alert-settings`);
        setAlertSettings(res.data);
      } catch (error) {
        console.error("Error fetching alert settings:", error);
      }
    };
    fetchAlertSettings();
  }, []);

  const saveAlertSettings = async (newSettings) => {
    setSavingAlertSettings(true);
    try {
      const res = await axios.put(`${API}/alert-settings`, newSettings);
      setAlertSettings(res.data);
      toast.success("Configurações de alertas guardadas");
    } catch (error) {
      toast.error("Erro ao guardar: " + getApiErrorMessage(error));
    } finally {
      setSavingAlertSettings(false);
    }
  };

  const handleRappelFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploadingRappelFile(true);
    try {
      const res = await axios.post(`${API}/rappel/targets/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(res.data.message);
      await fetchRappelTargets();
    } catch (error) {
      toast.error("Erro ao importar: " + getApiErrorMessage(error));
    } finally {
      setUploadingRappelFile(false);
      if (rappelFileInputRef.current) rappelFileInputRef.current.value = '';
    }
  };

  const startEditRappel = (target) => {
    setEditingRappelNumber(target.client_number);
    setRappelEditForm({
      client_name: target.client_name,
      tiers: target.tiers.map(t => ({ objetivo: String(t.objetivo), percentual: String((t.percentual * 100).toFixed(2)) }))
    });
  };

  const saveEditRappel = async () => {
    try {
      const payload = {
        client_number: editingRappelNumber,
        client_name: rappelEditForm.client_name,
        tiers: rappelEditForm.tiers.map(t => ({ objetivo: parseFloat(t.objetivo) || 0, percentual: (parseFloat(t.percentual) || 0) / 100 }))
      };
      await axios.put(`${API}/rappel/targets/${editingRappelNumber}`, payload);
      toast.success("Distribuidor atualizado");
      setEditingRappelNumber(null);
      await fetchRappelTargets();
    } catch (error) {
      toast.error("Erro ao guardar: " + getApiErrorMessage(error));
    }
  };

  const deleteRappelTarget = async (clientNumber) => {
    if (!window.confirm("Eliminar este distribuidor do Rappel?")) return;
    try {
      await axios.delete(`${API}/rappel/targets/${clientNumber}`);
      toast.success("Distribuidor eliminado");
      await fetchRappelTargets();
    } catch (error) {
      toast.error("Erro ao eliminar");
    }
  };

  const createRappelTarget = async () => {
    if (!rappelNewForm.client_number || !rappelNewForm.client_name) {
      toast.error("Número e nome são obrigatórios");
      return;
    }
    const tiers = rappelNewForm.tiers
      .filter(t => t.objetivo && t.percentual)
      .map(t => ({ objetivo: parseFloat(t.objetivo), percentual: parseFloat(t.percentual) / 100 }));
    if (tiers.length === 0) {
      toast.error("Adicione pelo menos um escalão (Objetivo + Percentual)");
      return;
    }
    try {
      await axios.post(`${API}/rappel/targets`, {
        client_number: rappelNewForm.client_number,
        client_name: rappelNewForm.client_name,
        tiers
      });
      toast.success("Distribuidor adicionado");
      setShowRappelAddForm(false);
      setRappelNewForm({ client_number: '', client_name: '', tiers: emptyRappelTiers() });
      await fetchRappelTargets();
    } catch (error) {
      toast.error("Erro ao adicionar: " + getApiErrorMessage(error));
    }
  };

  // Per-client Alert Settings handlers
  const openClientAlertsDialog = async () => {
    setShowClientAlertsDialog(true);
    setLoadingClientAlertSettings(true);
    try {
      const res = await axios.get(`${API}/alert-settings/clients`);
      setClientAlertSettings(res.data || []);
    } catch (error) {
      toast.error("Erro ao carregar clientes: " + getApiErrorMessage(error));
    } finally {
      setLoadingClientAlertSettings(false);
    }
  };

  const updateClientAlertFrequency = async (clientId, days) => {
    setSavingClientAlertId(clientId);
    try {
      await axios.put(`${API}/alert-settings/clients/${clientId}`, { visit_frequency_days: parseInt(days) });
      setClientAlertSettings(prev => prev.map(c => c.client_id === clientId ? { ...c, visit_frequency_days: parseInt(days), is_custom: true } : c));
      toast.success("Frequência de visita atualizada");
    } catch (error) {
      toast.error("Erro ao guardar: " + getApiErrorMessage(error));
    } finally {
      setSavingClientAlertId(null);
    }
  };

  const filteredClientAlertSettings = clientAlertSettings.filter(c =>
    c.client_name.toLowerCase().includes(clientAlertSearch.toLowerCase())
  );

  // Settings sections configuration
  const settingsSections = [
    {
      id: 'profile',
      title: 'Perfil',
      subtitle: profile.nome || 'Informações pessoais',
      icon: User,
      iconBg: 'bg-cyan-100 dark:bg-cyan-900/30',
      iconColor: 'text-cyan-600'
    },
    {
      id: 'vendas_upload',
      title: 'Carga de Vendas',
      subtitle: vendasLastUpdated ? `Atualizado ${new Date(vendasLastUpdated).toLocaleDateString('pt-PT')}` : 'Nenhum dado carregado',
      icon: ChartLineUp,
      iconBg: 'bg-teal-100 dark:bg-teal-900/30',
      iconColor: 'text-teal-600'
    },
    {
      id: 'encomendas_upload',
      title: 'Carga de Encomendas Diárias (PDFs)',
      subtitle: encomendasLastUpdated ? `Atualizado ${new Date(encomendasLastUpdated).toLocaleDateString('pt-PT')}` : 'Nenhum pedido carregado',
      icon: Truck,
      iconBg: 'bg-orange-100 dark:bg-orange-900/30',
      iconColor: 'text-orange-600'
    },
    {
      id: 'price_list',
      title: 'Actualizar Lista de Preços',
      subtitle: priceListMeta?.last_updated ? `Atualizado ${new Date(priceListMeta.last_updated).toLocaleDateString('pt-PT')}` : 'Nenhuma atualização registada',
      icon: Tag,
      iconBg: 'bg-violet-100 dark:bg-violet-900/30',
      iconColor: 'text-violet-600'
    },
    {
      id: 'price_outras_escolhas',
      title: 'Actualizar Preços Outras Escolhas',
      subtitle: priceExtrasMeta?.outras_escolhas_update?.last_updated ? `Atualizado ${new Date(priceExtrasMeta.outras_escolhas_update.last_updated).toLocaleDateString('pt-PT')}` : 'Nenhuma atualização registada',
      icon: Percent,
      iconBg: 'bg-rose-100 dark:bg-rose-900/30',
      iconColor: 'text-rose-600'
    },
    {
      id: 'price_volume',
      title: 'Actualizar Preços por Volume',
      subtitle: (priceExtrasMeta?.volume_2tier_update?.last_updated || priceExtrasMeta?.volume_1tier_update?.last_updated)
        ? `Atualizado ${new Date(priceExtrasMeta?.volume_2tier_update?.last_updated || priceExtrasMeta?.volume_1tier_update?.last_updated).toLocaleDateString('pt-PT')}`
        : 'Nenhuma atualização registada',
      icon: Truck,
      iconBg: 'bg-indigo-100 dark:bg-indigo-900/30',
      iconColor: 'text-indigo-600'
    },
    {
      id: 'price_special_campaign',
      title: 'Actualizar Preços Especiais',
      subtitle: priceExtrasMeta?.special_campaign_update?.last_updated ? `Atualizado ${new Date(priceExtrasMeta.special_campaign_update.last_updated).toLocaleDateString('pt-PT')}` : 'Nenhuma atualização registada',
      icon: FileXls,
      iconBg: 'bg-amber-100 dark:bg-amber-900/30',
      iconColor: 'text-amber-600'
    },
    {
      id: 'price_group_table',
      title: 'Actualizar Tabela de Grupos',
      subtitle: priceExtrasMeta?.group_table_update?.last_updated ? `Atualizado ${new Date(priceExtrasMeta.group_table_update.last_updated).toLocaleDateString('pt-PT')}` : 'Nenhuma atualização registada',
      icon: HashStraight,
      iconBg: 'bg-teal-100 dark:bg-teal-900/30',
      iconColor: 'text-teal-600'
    },
    {
      id: 'security',
      title: 'PIN da Secção Privada',
      subtitle: settings.pinEnabled ? (t('settings_pin_enabled') || 'PIN ativo') : (t('settings_pin_disabled') || 'PIN inativo'),
      icon: Shield,
      iconBg: 'bg-amber-100 dark:bg-amber-900/30',
      iconColor: 'text-amber-600'
    },
    {
      id: 'biometric',
      title: 'Face ID (Secção Privada)',
      subtitle: settings.biometricEnabled ? 'Ativo' : 'Configurar',
      icon: BiometricIcon,
      iconBg: 'bg-purple-100 dark:bg-purple-900/30',
      iconColor: 'text-purple-600',
      hidden: !settings.pinEnabled || !biometricAvailable
    },
    {
      id: 'password_access',
      title: 'Senha e Acesso',
      subtitle: passkeys.length > 0 ? `Senha + ${passkeys.length} Face ID/Touch ID` : 'Alterar senha, Face ID / Touch ID',
      icon: Key,
      iconBg: 'bg-cyan-100 dark:bg-cyan-900/30',
      iconColor: 'text-cyan-600'
    },
    {
      id: 'language',
      title: t('settings_language') || 'Idioma',
      subtitle: languages.find(l => l.code === settings.language)?.name || 'Português',
      icon: Translate,
      iconBg: 'bg-blue-100 dark:bg-blue-900/30',
      iconColor: 'text-blue-600'
    },
    {
      id: 'catalog',
      title: 'Gerir Catálogos',
      subtitle: catalogs.length > 0 ? `${catalogs.length} catálogo${catalogs.length > 1 ? 's' : ''}` : 'Nenhum catálogo carregado',
      icon: BookOpen,
      iconBg: 'bg-red-100 dark:bg-red-900/30',
      iconColor: 'text-red-500'
    },
    {
      id: 'data',
      title: t('settings_data') || 'Dados',
      subtitle: t('settings_data_desc') || 'Exportar e limpar dados',
      icon: CloudArrowDown,
      iconBg: 'bg-teal-100 dark:bg-teal-900/30',
      iconColor: 'text-teal-600'
    },
    {
      id: 'chatbot',
      title: 'Chatbot IA',
      subtitle: `${pdfTexts.length} documentos indexados`,
      icon: Robot,
      iconBg: 'bg-emerald-100 dark:bg-emerald-900/30',
      iconColor: 'text-emerald-600'
    },
    {
      id: 'rappel',
      title: 'Objetivos de Rappel',
      subtitle: `${rappelTargets.length} distribuidor(es)`,
      icon: Percent,
      iconBg: 'bg-orange-100 dark:bg-orange-900/30',
      iconColor: 'text-orange-600'
    },
    {
      id: 'alerts',
      title: 'Gestão de Alertas',
      subtitle: 'Visitas: por cliente',
      icon: Bell,
      iconBg: 'bg-red-100 dark:bg-red-900/30',
      iconColor: 'text-red-600'
    },
    {
      id: 'users',
      title: 'Gestão de Utilizadores',
      subtitle: isAdmin ? `${adminUsers.length || ''} acesso(s) à aplicação` : 'Acesso restrito',
      icon: Shield,
      iconBg: 'bg-indigo-100 dark:bg-indigo-900/30',
      iconColor: 'text-indigo-600',
      hidden: !isAdmin
    },
    {
      id: 'storage',
      title: 'Armazenamento',
      subtitle: storageStats ? `${(storageStats.db_data_size_bytes / 1024 / 1024).toFixed(0)} MB usados na base de dados` : 'Espaço em disco',
      icon: Database,
      iconBg: 'bg-amber-100 dark:bg-amber-900/30',
      iconColor: 'text-amber-600',
      hidden: !isAdmin
    },
  ];

  // Render settings section content
  const renderSettingsSectionContent = () => {
    switch (activeSection) {
      case 'profile':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nome" className="flex items-center gap-2">
                  <User size={16} className="text-stone-400" />
                  Nome
                </Label>
                <Input
                  id="nome"
                  value={profile.nome}
                  onChange={(e) => setProfile(prev => ({ ...prev, nome: e.target.value }))}
                  placeholder="Seu nome completo"
                  data-testid="input-nome"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="marca" className="flex items-center gap-2">
                  <Storefront size={16} className="text-stone-400" />
                  Marca
                </Label>
                <Input
                  id="marca"
                  value={profile.marca}
                  onChange={(e) => setProfile(prev => ({ ...prev, marca: e.target.value }))}
                  placeholder="Nome da marca/empresa"
                  data-testid="input-marca"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="zona" className="flex items-center gap-2">
                  <MapPin size={16} className="text-stone-400" />
                  Zona
                </Label>
                <Input
                  id="zona"
                  value={profile.zona}
                  onChange={(e) => setProfile(prev => ({ ...prev, zona: e.target.value }))}
                  placeholder="Região/Zona de atuação"
                  data-testid="input-zona"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Envelope size={16} className="text-stone-400" />
                  E-mail
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="seu@email.com"
                  data-testid="input-email"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="contato" className="flex items-center gap-2">
                  <Phone size={16} className="text-stone-400" />
                  Contato
                </Label>
                <Input
                  id="contato"
                  value={profile.contato}
                  onChange={(e) => setProfile(prev => ({ ...prev, contato: e.target.value }))}
                  placeholder="+351 912 345 678"
                  data-testid="input-contato"
                />
              </div>
            </div>
            <Button 
              className="btn-primary w-full"
              onClick={saveProfile}
              disabled={savingProfile}
              data-testid="btn-save-profile"
            >
              <FloppyDisk size={18} className="mr-1" />
              {savingProfile ? 'A guardar...' : 'Guardar'}
            </Button>
          </div>
        );

      case 'vendas_upload':
        return (
          <div className="space-y-4">
            <p className="text-sm text-stone-500 dark:text-stone-400">
              Carregue os ficheiros GPP_Monthly_Sales (PDF), GPP_Hit_Parade (Excel/PDF), GPP_Open_Orders_Cust/Coll (PDF) e os ficheiros "vendas em € 2024-2025" / "Vendas em M2 2024-2025" (PDF) para atualizar a Análise de Vendas.
            </p>
            {vendasLastUpdated && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-2.5 text-sm text-blue-700 dark:text-blue-300">
                Última atualização: <strong>{new Date(vendasLastUpdated).toLocaleString('pt-PT')}</strong>
              </div>
            )}

            <div className="bg-stone-50 dark:bg-stone-800 rounded-lg p-4 space-y-2">
              <label className="text-sm font-medium text-stone-700 dark:text-stone-200">Objetivo Mensal (€)</label>
              <p className="text-xs text-stone-400">
                Usado na "Previsão de Fecho" da Vendas Mensais quando o ficheiro Monthly Sales não tem um objetivo definido.
              </p>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min="0"
                  step="100"
                  placeholder="Ex: 120000"
                  value={monthlyGoalInput}
                  onChange={(e) => setMonthlyGoalInput(e.target.value)}
                  data-testid="input-objetivo-mensal"
                />
                <Button
                  onClick={handleSaveMonthlyGoal}
                  disabled={savingMonthlyGoal}
                  className="bg-[#009392] hover:bg-[#007a7a] flex-shrink-0"
                  data-testid="btn-guardar-objetivo-mensal"
                >
                  Guardar
                </Button>
              </div>
              {savedMonthlyGoal != null && (
                <p className="text-xs text-teal-600">Objetivo atual: {savedMonthlyGoal.toLocaleString('pt-PT', { minimumFractionDigits: 2 })} €</p>
              )}
            </div>

            <div
              className="border-2 border-dashed border-stone-300 dark:border-stone-700 rounded-lg p-6 text-center hover:border-[#009392] transition-colors cursor-pointer"
              onClick={() => vendasFileInputRef.current?.click()}
              data-testid="profile-vendas-dropzone"
            >
              <input
                ref={vendasFileInputRef}
                type="file"
                multiple
                accept=".pdf,.xlsx,.xls"
                onChange={handleVendasFileUpload}
                className="hidden"
                data-testid="profile-vendas-file-input"
              />
              <Upload size={36} className="mx-auto text-stone-400 mb-2" />
              <p className="text-stone-600 dark:text-stone-300 font-medium text-sm">Clique ou arraste ficheiros GPP</p>
              <p className="text-stone-400 text-xs mt-1">GPP_Monthly_Sales.pdf, GPP_Hit_Parade_*.xlsx/pdf, GPP_Open_Orders_Cust/Coll.pdf, vendas em € 2024-2025.pdf, Vendas em M2 2024-2025.pdf</p>
            </div>

            {vendasFiles.length > 0 && (
              <div className="space-y-2">
                {vendasFiles.map((file, index) => (
                  <div key={index} className="flex items-center gap-3 bg-stone-50 dark:bg-stone-800 rounded-lg p-3">
                    <div className="flex-shrink-0">{getVendasFileIcon(file.name)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-stone-700 dark:text-stone-200 break-all">{file.name}</p>
                      <p className="text-xs text-stone-400">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button
                      onClick={() => removeVendasFile(index)}
                      className="p-1.5 hover:bg-red-100 rounded-full text-stone-400 hover:text-red-500 transition-colors flex-shrink-0"
                      data-testid={`profile-vendas-remove-file-${index}`}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {vendasFiles.length > 0 && (
              <Button
                onClick={parseVendasData}
                disabled={parsingVendas}
                className="w-full bg-gradient-to-r from-[#009392] to-[#00b3b0] hover:from-[#007a7a] hover:to-[#009392]"
                data-testid="profile-parse-vendas-btn"
              >
                {parsingVendas ? (
                  <>
                    <LoadingSpinner size={20} className="mr-2" />
                    Processando...
                  </>
                ) : (
                  <>
                    <ChartLineUp size={20} className="mr-2" />
                    Processar Dados de Vendas
                  </>
                )}
              </Button>
            )}
          </div>
        );

      case 'encomendas_upload':
        return (
          <div className="space-y-4">
            <p className="text-sm text-stone-500 dark:text-stone-400">
              Carregue os PDFs "Lista de Situação Encomendas" (1 PDF por cliente) para atualizar as Encomendas Pendentes. <strong>Cada carga substitui totalmente os pedidos anteriores.</strong>
            </p>
            {encomendasLastUpdated && (
              <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg px-4 py-2.5 text-sm text-orange-700 dark:text-orange-300">
                Última atualização: <strong>{new Date(encomendasLastUpdated).toLocaleString('pt-PT')}</strong>
              </div>
            )}
            <div
              className="border-2 border-dashed border-stone-300 dark:border-stone-700 rounded-lg p-6 text-center hover:border-orange-500 transition-colors cursor-pointer"
              onClick={() => encomendaFileInputRef.current?.click()}
              data-testid="profile-encomendas-dropzone"
            >
              <input
                ref={encomendaFileInputRef}
                type="file"
                multiple
                accept=".pdf"
                onChange={handleEncomendaFileUpload}
                className="hidden"
                data-testid="profile-encomendas-file-input"
              />
              <Upload size={36} className="mx-auto text-stone-400 mb-2" />
              <p className="text-stone-600 dark:text-stone-300 font-medium text-sm">Clique ou arraste os PDFs das encomendas</p>
              <p className="text-stone-400 text-xs mt-1">Ex: 0019997170-NOME DO CLIENTE.pdf (1 ficheiro por cliente, pode selecionar vários)</p>
            </div>

            {encomendaFiles.length > 0 && (
              <div className="space-y-2">
                {encomendaFiles.map((file, index) => (
                  <div key={index} className="flex items-center gap-3 bg-stone-50 dark:bg-stone-800 rounded-lg p-3">
                    <div className="flex-shrink-0">{getVendasFileIcon(file.name)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-stone-700 dark:text-stone-200 break-all">{file.name}</p>
                      <p className="text-xs text-stone-400">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button
                      onClick={() => removeEncomendaFile(index)}
                      className="p-1.5 hover:bg-red-100 rounded-full text-stone-400 hover:text-red-500 transition-colors flex-shrink-0"
                      data-testid={`profile-encomendas-remove-file-${index}`}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {encomendaFiles.length > 0 && (
              <Button
                onClick={uploadEncomendasData}
                disabled={parsingEncomendas}
                className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
                data-testid="profile-parse-encomendas-btn"
              >
                {parsingEncomendas ? (
                  <>
                    <LoadingSpinner size={20} className="mr-2" />
                    Processando...
                  </>
                ) : (
                  <>
                    <Truck size={20} className="mr-2" />
                    Processar Encomendas ({encomendaFiles.length})
                  </>
                )}
              </Button>
            )}
          </div>
        );

      case 'price_list':
        return (
          <div className="space-y-4" data-testid="price-list-section">
            <p className="text-sm text-stone-500 dark:text-stone-400">
              Carregue um novo ficheiro Excel com a lista de preços atualizada. Os preços e restantes dados das referências existentes são substituídos, novas referências são adicionadas automaticamente, e referências que deixaram de existir pedem confirmação antes de serem removidas.
            </p>
            {priceListMeta?.last_updated && (
              <div className="bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-lg px-4 py-2.5 text-sm text-violet-700 dark:text-violet-300" data-testid="price-list-last-update">
                Última atualização: <strong>{new Date(priceListMeta.last_updated).toLocaleString('pt-PT')}</strong>
                {priceListMeta.updated_by && <> por <strong>{priceListMeta.updated_by}</strong></>}
                <div className="text-xs mt-1 text-violet-600 dark:text-violet-400">
                  {priceListMeta.added_count || 0} adicionado(s) · {priceListMeta.updated_count || 0} atualizado(s) · {priceListMeta.removed_count || 0} removido(s)
                </div>
              </div>
            )}
            <div
              className="border-2 border-dashed border-stone-300 dark:border-stone-700 rounded-lg p-6 text-center hover:border-[#009392] transition-colors cursor-pointer"
              onClick={() => priceListFileInputRef.current?.click()}
              data-testid="price-list-dropzone"
            >
              <input
                ref={priceListFileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handlePriceListFileSelect}
                className="hidden"
                data-testid="input-price-list-upload"
              />
              {uploadingPriceList ? (
                <>
                  <LoadingSpinner size={28} className="mx-auto mb-2" />
                  <p className="text-stone-500 text-sm">A processar...</p>
                </>
              ) : (
                <>
                  <Upload size={36} className="mx-auto text-stone-400 mb-2" />
                  <p className="text-stone-600 dark:text-stone-300 font-medium text-sm">Clique para carregar o novo ficheiro Excel</p>
                  <p className="text-stone-400 text-xs mt-1">Descrição Produto, Cor, Formato, Acabamento/Superfície, Espessura, Série, Marca, Tipo de Utilização, Preço, Moeda, Grupo Preço, Unidade de Medida, Peças/m2/kg por Caixa e Palete, Qualidade, EAN, País de Origem.</p>
                </>
              )}
            </div>
          </div>
        );

      case 'price_outras_escolhas':
        return (
          <div className="space-y-4" data-testid="price-outras-escolhas-section">
            <p className="text-sm text-stone-500 dark:text-stone-400">
              Carregue a "Tabela Económicos" (PDF) com os preços de 2ª e 3ª Escolha por formato, tipo de material e se é retificado. Esta tabela substitui por completo a anterior.
            </p>
            {priceExtrasMeta?.outras_escolhas_update?.last_updated && (
              <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-lg px-4 py-2.5 text-sm text-rose-700 dark:text-rose-300" data-testid="outras-escolhas-last-update">
                Última atualização: <strong>{new Date(priceExtrasMeta.outras_escolhas_update.last_updated).toLocaleString('pt-PT')}</strong>
                {priceExtrasMeta.outras_escolhas_update.updated_by && <> por <strong>{priceExtrasMeta.outras_escolhas_update.updated_by}</strong></>}
                <div className="text-xs mt-1 text-rose-600 dark:text-rose-400">{priceExtrasMeta.outras_escolhas_update.count || 0} registo(s)</div>
              </div>
            )}
            <div
              className="border-2 border-dashed border-stone-300 dark:border-stone-700 rounded-lg p-6 text-center hover:border-[#009392] transition-colors cursor-pointer"
              onClick={() => outrasEscolhasFileInputRef.current?.click()}
              data-testid="outras-escolhas-dropzone"
            >
              <input
                ref={outrasEscolhasFileInputRef}
                type="file"
                accept=".pdf"
                onChange={(e) => handlePriceExtraUpload(e, 'outras-escolhas', 'outras_escolhas_update')}
                className="hidden"
                data-testid="input-outras-escolhas-upload"
              />
              {uploadingExtra === 'outras_escolhas_update' ? (
                <>
                  <LoadingSpinner size={28} className="mx-auto mb-2" />
                  <p className="text-stone-500 text-sm">A processar...</p>
                </>
              ) : (
                <>
                  <Upload size={36} className="mx-auto text-stone-400 mb-2" />
                  <p className="text-stone-600 dark:text-stone-300 font-medium text-sm">Clique para carregar o PDF "Tabela Económicos"</p>
                </>
              )}
            </div>
          </div>
        );

      case 'price_volume':
        return (
          <div className="space-y-5" data-testid="price-volume-section">
            <p className="text-sm text-stone-500 dark:text-stone-400">
              Carregue os PDFs "Tabela Líquida" com os preços por volume de paletes. Existem dois ficheiros distintos: um com dois escalões (2 e 5 paletes por referência) e outro com escalão único (mínimo 10 paletes, 2 por referência).
            </p>

            <div>
              <p className="text-xs font-bold text-stone-400 uppercase tracking-wide mb-2">Dois Escalões (2 e 5 Paletes)</p>
              {priceExtrasMeta?.volume_2tier_update?.last_updated && (
                <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg px-4 py-2 text-sm text-indigo-700 dark:text-indigo-300 mb-2" data-testid="volume-2tier-last-update">
                  Última atualização: <strong>{new Date(priceExtrasMeta.volume_2tier_update.last_updated).toLocaleString('pt-PT')}</strong>
                  <div className="text-xs mt-0.5 text-indigo-600 dark:text-indigo-400">{priceExtrasMeta.volume_2tier_update.count || 0} registo(s)</div>
                </div>
              )}
              <div
                className="border-2 border-dashed border-stone-300 dark:border-stone-700 rounded-lg p-4 text-center hover:border-[#009392] transition-colors cursor-pointer"
                onClick={() => volume2tierFileInputRef.current?.click()}
                data-testid="volume-2tier-dropzone"
              >
                <input
                  ref={volume2tierFileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={(e) => handlePriceExtraUpload(e, 'volume-2tier', 'volume_2tier_update')}
                  className="hidden"
                  data-testid="input-volume-2tier-upload"
                />
                {uploadingExtra === 'volume_2tier_update' ? (
                  <LoadingSpinner size={24} className="mx-auto" />
                ) : (
                  <p className="text-stone-600 dark:text-stone-300 text-sm font-medium">Clique para carregar o PDF (2 Escalões)</p>
                )}
              </div>
            </div>

            <div>
              <p className="text-xs font-bold text-stone-400 uppercase tracking-wide mb-2">Escalão Único (+10 Paletes)</p>
              {priceExtrasMeta?.volume_1tier_update?.last_updated && (
                <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg px-4 py-2 text-sm text-indigo-700 dark:text-indigo-300 mb-2" data-testid="volume-1tier-last-update">
                  Última atualização: <strong>{new Date(priceExtrasMeta.volume_1tier_update.last_updated).toLocaleString('pt-PT')}</strong>
                  <div className="text-xs mt-0.5 text-indigo-600 dark:text-indigo-400">{priceExtrasMeta.volume_1tier_update.count || 0} registo(s)</div>
                </div>
              )}
              <div
                className="border-2 border-dashed border-stone-300 dark:border-stone-700 rounded-lg p-4 text-center hover:border-[#009392] transition-colors cursor-pointer"
                onClick={() => volume1tierFileInputRef.current?.click()}
                data-testid="volume-1tier-dropzone"
              >
                <input
                  ref={volume1tierFileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={(e) => handlePriceExtraUpload(e, 'volume-1tier', 'volume_1tier_update')}
                  className="hidden"
                  data-testid="input-volume-1tier-upload"
                />
                {uploadingExtra === 'volume_1tier_update' ? (
                  <LoadingSpinner size={24} className="mx-auto" />
                ) : (
                  <p className="text-stone-600 dark:text-stone-300 text-sm font-medium">Clique para carregar o PDF (Escalão Único)</p>
                )}
              </div>
            </div>
          </div>
        );

      case 'price_special_campaign':
        return (
          <div className="space-y-4" data-testid="price-special-campaign-section">
            <p className="text-sm text-stone-500 dark:text-stone-400">
              Carregue o PDF "Preços Especiais" da campanha em vigor (ex: OAK e DECK). O nome da campanha é identificado automaticamente a partir do título do PDF.
            </p>
            {priceExtrasMeta?.special_campaign_update?.last_updated && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-2.5 text-sm text-amber-700 dark:text-amber-300" data-testid="special-campaign-last-update">
                Campanha: <strong>{priceExtrasMeta.special_campaign_update.campaign_name}</strong><br />
                Última atualização: <strong>{new Date(priceExtrasMeta.special_campaign_update.last_updated).toLocaleString('pt-PT')}</strong>
                <div className="text-xs mt-1 text-amber-600 dark:text-amber-400">{priceExtrasMeta.special_campaign_update.count || 0} registo(s)</div>
              </div>
            )}
            <div
              className="border-2 border-dashed border-stone-300 dark:border-stone-700 rounded-lg p-6 text-center hover:border-[#009392] transition-colors cursor-pointer"
              onClick={() => specialCampaignFileInputRef.current?.click()}
              data-testid="special-campaign-dropzone"
            >
              <input
                ref={specialCampaignFileInputRef}
                type="file"
                accept=".pdf"
                onChange={(e) => handlePriceExtraUpload(e, 'special-campaign', 'special_campaign_update')}
                className="hidden"
                data-testid="input-special-campaign-upload"
              />
              {uploadingExtra === 'special_campaign_update' ? (
                <>
                  <LoadingSpinner size={28} className="mx-auto mb-2" />
                  <p className="text-stone-500 text-sm">A processar...</p>
                </>
              ) : (
                <>
                  <Upload size={36} className="mx-auto text-stone-400 mb-2" />
                  <p className="text-stone-600 dark:text-stone-300 font-medium text-sm">Clique para carregar o PDF "Preços Especiais"</p>
                </>
              )}
            </div>
          </div>
        );

      case 'price_group_table':
        return (
          <div className="space-y-4" data-testid="price-group-table-section">
            <p className="text-sm text-stone-500 dark:text-stone-400">
              Carregue o PDF "Tabela de Preços" de grupos (códigos GR-XXXX preço/m² e GP-XXXX preço/unidade). Esta tabela alimenta a "Pesquisa por Grupo" na página de Preços.
            </p>
            {priceExtrasMeta?.group_table_update?.last_updated && (
              <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg px-4 py-2.5 text-sm text-teal-700 dark:text-teal-300" data-testid="group-table-last-update">
                {priceExtrasMeta.group_table_update.edicao && <>Edição: <strong>{priceExtrasMeta.group_table_update.edicao}</strong><br /></>}
                Última atualização: <strong>{new Date(priceExtrasMeta.group_table_update.last_updated).toLocaleString('pt-PT')}</strong>
                {priceExtrasMeta.group_table_update.updated_by && <> por <strong>{priceExtrasMeta.group_table_update.updated_by}</strong></>}
                <div className="text-xs mt-1 text-teal-600 dark:text-teal-400">{priceExtrasMeta.group_table_update.count || 0} código(s) de grupo</div>
              </div>
            )}
            <div
              className="border-2 border-dashed border-stone-300 dark:border-stone-700 rounded-lg p-6 text-center hover:border-[#009392] transition-colors cursor-pointer"
              onClick={() => groupTableFileInputRef.current?.click()}
              data-testid="group-table-dropzone"
            >
              <input
                ref={groupTableFileInputRef}
                type="file"
                accept=".pdf"
                onChange={(e) => handlePriceExtraUpload(e, 'group-table', 'group_table_update')}
                className="hidden"
                data-testid="input-group-table-upload"
              />
              {uploadingExtra === 'group_table_update' ? (
                <>
                  <LoadingSpinner size={28} className="mx-auto mb-2" />
                  <p className="text-stone-500 text-sm">A processar...</p>
                </>
              ) : (
                <>
                  <Upload size={36} className="mx-auto text-stone-400 mb-2" />
                  <p className="text-stone-600 dark:text-stone-300 font-medium text-sm">Clique para carregar o PDF "Tabela de Preços" de grupos</p>
                </>
              )}
            </div>
          </div>
        );

      case 'security':
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <span className={`text-sm font-medium px-3 py-1 rounded-full ${
                settings.pinEnabled 
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                  : 'bg-stone-100 text-stone-500 dark:bg-stone-800 dark:text-stone-400'
              }`}>
                {settings.pinEnabled ? t('settings_pin_enabled') : t('settings_pin_disabled')}
              </span>
            </div>
            <p className="text-sm text-stone-500 dark:text-stone-400">
              {t('settings_security_desc') || 'Proteja a Secção Privada com um PIN de 4 dígitos'}
            </p>
            <div className="flex flex-wrap gap-2 pt-2">
              {settings.pinEnabled ? (
                <>
                  <Button 
                    variant="outline" 
                    onClick={() => openPinDialog('change')}
                    data-testid="btn-change-pin"
                  >
                    {t('settings_change_pin') || 'Alterar PIN'}
                  </Button>
                  <Button 
                    variant="outline" 
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={() => openPinDialog('remove')}
                    data-testid="btn-remove-pin"
                  >
                    {t('settings_remove_pin') || 'Remover PIN'}
                  </Button>
                </>
              ) : (
                <Button 
                  className="btn-primary"
                  onClick={() => openPinDialog('set')}
                  data-testid="btn-set-pin"
                >
                  {t('settings_set_pin') || 'Definir PIN'}
                </Button>
              )}
            </div>
          </div>
        );

      case 'biometric':
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <span className={`text-sm font-medium px-3 py-1 rounded-full ${
                settings.biometricEnabled 
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                  : 'bg-stone-100 text-stone-500 dark:bg-stone-800 dark:text-stone-400'
              }`}>
                {settings.biometricEnabled ? 'Ativo' : 'Inativo'}
              </span>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              {settings.biometricEnabled ? (
                <Button 
                  variant="outline" 
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  onClick={handleDisableBiometric}
                  data-testid="btn-disable-biometric"
                >
                  Desativar
                </Button>
              ) : (
                <Button 
                  className="btn-primary"
                  onClick={handleEnableBiometric}
                  disabled={enablingBiometric}
                  data-testid="btn-enable-biometric"
                >
                  {enablingBiometric ? 'A ativar...' : 'Configurar'}
                </Button>
              )}
            </div>
          </div>
        );

      case 'language':
        return (
          <div className="space-y-4">
            <p className="text-sm text-stone-500 dark:text-stone-400">
              {t('settings_language_desc') || 'Selecione o idioma da aplicação'}
            </p>
            <Select 
              value={settings.language} 
              onValueChange={(val) => updateSetting('language', val)}
            >
              <SelectTrigger className="w-full max-w-[250px]" data-testid="select-language">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code} data-testid={`lang-${lang.code}`}>
                    <span className="flex items-center gap-2">
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'catalog':
        return (
          <div className="space-y-4" data-testid="manage-catalogos-section">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-sm text-stone-500 dark:text-stone-400">
                Carregue vários catálogos PDF, dê-lhes um título e arraste para definir a ordem de prioridade — a mesma ordem aparece na biblioteca pública "Catálogos Gresart".
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                multiple
                onChange={handleCatalogFilesSelected}
                className="hidden"
                data-testid="input-select-catalog-files"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                data-testid="btn-add-catalogos"
              >
                <CloudArrowUp size={16} className="mr-1.5" />
                Carregar Catálogos
              </Button>
            </div>

            {catalogs.length === 0 ? (
              <div className="text-sm text-stone-400 border border-dashed border-stone-200 rounded-xl py-10 text-center">
                Ainda sem catálogos. Clique em "Carregar Catálogos" para adicionar os primeiros PDFs.
              </div>
            ) : (
              <DndContext sensors={catalogDragSensors} collisionDetection={closestCenter} onDragEnd={handleCatalogDragEnd}>
                <SortableContext items={catalogs.map((c) => c.id)} strategy={rectSortingStrategy}>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="manage-catalogos-grid">
                    {catalogs.map((catalog) => (
                      <SortableCatalogCard
                        key={catalog.id}
                        catalog={catalog}
                        onDelete={setDeleteCatalogId}
                        onRename={renameCatalog}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}

            {/* Dialog: pede um título a cada PDF selecionado antes de carregar */}
            <Dialog open={pendingCatalogFiles.length > 0} onOpenChange={(open) => !open && !catalogUploading && setPendingCatalogFiles([])}>
              <DialogContent className="max-w-lg" data-testid="dialog-catalog-titles">
                <DialogHeader>
                  <DialogTitle>Títulos dos Catálogos</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 max-h-[50vh] overflow-y-auto py-2">
                  {pendingCatalogFiles.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2" data-testid={`pending-catalog-row-${idx}`}>
                      <FilePdf size={20} className="text-red-500 flex-shrink-0" />
                      <Input
                        value={item.title}
                        onChange={(e) => updatePendingCatalogTitle(idx, e.target.value)}
                        placeholder="Título do catálogo"
                        disabled={catalogUploading}
                        data-testid={`input-pending-catalog-title-${idx}`}
                      />
                      {!catalogUploading && (
                        <button onClick={() => removePendingCatalogFile(idx)} className="text-stone-400 hover:text-red-500 flex-shrink-0" data-testid={`btn-remove-pending-catalog-${idx}`}>
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <DialogFooter>
                  {catalogUploadProgress && (
                    <p className="text-xs text-stone-400 mr-auto">A carregar {catalogUploadProgress.current}/{catalogUploadProgress.total}...</p>
                  )}
                  <Button variant="outline" onClick={() => setPendingCatalogFiles([])} disabled={catalogUploading}>Cancelar</Button>
                  <Button onClick={confirmCatalogUpload} disabled={catalogUploading} className="btn-primary" data-testid="btn-confirm-catalog-upload">
                    {catalogUploading ? 'A carregar...' : `Carregar ${pendingCatalogFiles.length} catálogo${pendingCatalogFiles.length > 1 ? 's' : ''}`}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <ConfirmDeleteDialog
              open={!!deleteCatalogId}
              onOpenChange={(open) => !open && setDeleteCatalogId(null)}
              onConfirm={handleDeleteCatalog}
              title="Eliminar catálogo"
              description="Tem a certeza que quer eliminar este catálogo? Esta ação não pode ser desfeita."
            />
          </div>
        );

      case 'data':
        return (
          <div className="space-y-4">
            <p className="text-sm text-stone-500 dark:text-stone-400">
              {t('settings_data_desc') || 'Gerir dados da aplicação'}
            </p>
            <div className="flex flex-wrap gap-2 pt-2">
              <Button 
                variant="outline" 
                onClick={handleExportData}
                data-testid="btn-export-data"
              >
                <CloudArrowDown size={16} className="mr-1" />
                {t('settings_export') || 'Exportar Dados'}
              </Button>
              <Button 
                variant="outline" 
                onClick={handleClearCache}
                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                data-testid="btn-clear-cache"
              >
                <Trash size={16} className="mr-1" />
                {t('settings_clear_cache') || 'Limpar Cache'}
              </Button>
            </div>
          </div>
        );

      case 'chatbot':
        return (
          <div className="space-y-4">
            <p className="text-sm text-stone-500 dark:text-stone-400">
              Carregar documentos (PDF, Excel) para que o chatbot possa responder perguntas sobre produtos, preços, packing, etc.
            </p>
            
            {/* Current indexed documents */}
            <div className="mt-4">
              <h4 className="text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
                Documentos Indexados ({pdfTexts.length})
              </h4>
              {loadingPdfs ? (
                <div className="text-sm text-stone-500">A carregar...</div>
              ) : pdfTexts.length === 0 ? (
                <div className="text-sm text-amber-600 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
                  Nenhum documento indexado. Carregue PDFs ou Excel para o chatbot poder responder.
                </div>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {pdfTexts.map((pdf, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-stone-50 dark:bg-stone-800 rounded-lg">
                      <div className="flex items-center gap-2 min-w-0">
                        <FilePdf size={18} className="text-red-500 flex-shrink-0" />
                        <span className="text-sm font-medium truncate">{pdf.pdf_name}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        <span className="text-xs text-stone-400">
                          {(pdf.char_count / 1000).toFixed(1)}k
                        </span>
                        <button
                          onClick={() => deletePdfText(pdf.pdf_name)}
                          className="text-stone-500 hover:text-red-500 transition-colors p-1"
                          data-testid={`btn-delete-pdf-${idx}`}
                          title="Eliminar este documento"
                          aria-label={`Eliminar documento ${pdf.pdf_name}`}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Upload Button */}
            <div className="pt-4">
              <input
                type="file"
                ref={pdfFileInputRef}
                onChange={handleChatbotFileUpload}
                accept=".pdf,.xlsx,.xls"
                multiple
                className="hidden"
                data-testid="input-upload-chatbot-file"
              />
              <Button 
                onClick={() => pdfFileInputRef.current?.click()}
                disabled={uploadingPdf}
                className="w-full btn-primary"
                data-testid="btn-upload-chatbot-file"
              >
                {uploadingPdf ? 'A processar...' : '📁 Carregar PDF ou Excel (pode selecionar vários)'}
              </Button>
              <p className="text-xs text-stone-400 mt-2 text-center">
                Catálogos, listas de preços, packing lists, tabelas de produtos...
              </p>
            </div>
            
            {/* Clear Button */}
            {pdfTexts.length > 0 && (
              <Button 
                variant="outline" 
                className="w-full text-red-500 hover:text-red-600 hover:bg-red-50"
                onClick={clearPdfTexts}
                data-testid="btn-clear-chatbot-docs"
              >
                <Trash size={16} className="mr-1" />
                Eliminar Todos os Documentos
              </Button>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const renderRappelAlertsContent = () => {
    switch (activeSection) {
      case 'rappel':
        return (
          <div className="space-y-4">
            <p className="text-sm text-stone-500 dark:text-stone-400">
              Carregue um ficheiro anual (Excel recomendado, PDF em modo experimental) com os objetivos de Rappel por distribuidor, ou edite manualmente os escalões existentes.
            </p>

            <div>
              <input
                ref={rappelFileInputRef}
                type="file"
                accept=".xlsx,.xls,.pdf"
                onChange={handleRappelFileUpload}
                className="hidden"
                data-testid="input-rappel-upload"
              />
              <Button
                variant="outline"
                onClick={() => rappelFileInputRef.current?.click()}
                disabled={uploadingRappelFile}
                className="w-full"
                data-testid="btn-upload-rappel-excel"
              >
                {uploadingRappelFile ? 'A importar...' : 'Carregar ficheiro anual de Rappel (Excel ou PDF)'}
              </Button>
              <p className="text-xs text-stone-400 mt-1">
                Excel - colunas esperadas: Numero, Nome, Objetivo1, Percentual1, Objetivo2, Percentual2, Objetivo3, Percentual3
              </p>
            </div>

            <div className="pt-4 border-t border-stone-200 dark:border-stone-700">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-stone-700 dark:text-stone-300">
                  Distribuidores ({rappelTargets.length})
                </h4>
                <Button size="sm" variant="outline" onClick={() => setShowRappelAddForm(!showRappelAddForm)} data-testid="btn-toggle-add-rappel">
                  <Plus size={14} className="mr-1" /> Novo
                </Button>
              </div>

              {showRappelAddForm && (
                <div className="p-3 mb-3 rounded-lg bg-stone-50 dark:bg-stone-800 space-y-2" data-testid="rappel-add-form">
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Número" value={rappelNewForm.client_number}
                      onChange={(e) => setRappelNewForm(prev => ({ ...prev, client_number: e.target.value }))}
                      data-testid="input-new-rappel-number" />
                    <Input placeholder="Nome" value={rappelNewForm.client_name}
                      onChange={(e) => setRappelNewForm(prev => ({ ...prev, client_name: e.target.value }))}
                      data-testid="input-new-rappel-name" />
                  </div>
                  {rappelNewForm.tiers.map((tier, idx) => (
                    <div key={idx} className="grid grid-cols-2 gap-2">
                      <Input placeholder={`Objetivo Escalão ${idx + 1} (€)`} type="number" value={tier.objetivo}
                        onChange={(e) => setRappelNewForm(prev => ({ ...prev, tiers: prev.tiers.map((t, i) => i === idx ? { ...t, objetivo: e.target.value } : t) }))}
                        data-testid={`input-new-rappel-objetivo-${idx}`} />
                      <Input placeholder={`% Escalão ${idx + 1}`} type="number" value={tier.percentual}
                        onChange={(e) => setRappelNewForm(prev => ({ ...prev, tiers: prev.tiers.map((t, i) => i === idx ? { ...t, percentual: e.target.value } : t) }))}
                        data-testid={`input-new-rappel-percentual-${idx}`} />
                    </div>
                  ))}
                  <Button size="sm" className="btn-primary w-full" onClick={createRappelTarget} data-testid="btn-save-new-rappel">
                    Guardar Distribuidor
                  </Button>
                </div>
              )}

              {loadingRappelTargets ? (
                <p className="text-sm text-stone-400">A carregar...</p>
              ) : rappelTargets.length === 0 ? (
                <p className="text-sm text-stone-400">Nenhum distribuidor configurado.</p>
              ) : (
                <div className="space-y-2">
                  {rappelTargets.map((target) => (
                    <div key={target.client_number} className="p-3 rounded-lg bg-stone-50 dark:bg-stone-800" data-testid={`rappel-target-${target.client_number}`}>
                      {editingRappelNumber === target.client_number ? (
                        <div className="space-y-2">
                          <Input value={rappelEditForm.client_name}
                            onChange={(e) => setRappelEditForm(prev => ({ ...prev, client_name: e.target.value }))}
                            data-testid={`input-edit-rappel-name-${target.client_number}`} />
                          {rappelEditForm.tiers.map((tier, idx) => (
                            <div key={idx} className="grid grid-cols-2 gap-2">
                              <Input placeholder={`Objetivo ${idx + 1}`} type="number" value={tier.objetivo}
                                onChange={(e) => setRappelEditForm(prev => ({ ...prev, tiers: prev.tiers.map((t, i) => i === idx ? { ...t, objetivo: e.target.value } : t) }))} />
                              <Input placeholder={`% ${idx + 1}`} type="number" value={tier.percentual}
                                onChange={(e) => setRappelEditForm(prev => ({ ...prev, tiers: prev.tiers.map((t, i) => i === idx ? { ...t, percentual: e.target.value } : t) }))} />
                            </div>
                          ))}
                          <div className="flex gap-2">
                            <Button size="sm" className="btn-primary" onClick={saveEditRappel} data-testid={`btn-save-rappel-${target.client_number}`}>
                              <Check size={14} className="mr-1" /> Guardar
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingRappelNumber(null)}>
                              <X size={14} className="mr-1" /> Cancelar
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-stone-800 dark:text-stone-100 truncate">{target.client_name}</p>
                            <p className="text-xs text-stone-400">Nº {target.client_number}</p>
                            <p className="text-xs text-stone-500 mt-1">
                              {target.tiers.map((t, i) => `E${i + 1}: €${t.objetivo.toLocaleString('pt-PT')} (${(t.percentual * 100).toFixed(1)}%)`).join(' · ')}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button onClick={() => startEditRappel(target)} className="p-1.5 text-stone-400 hover:text-[#009392] rounded" data-testid={`btn-edit-rappel-${target.client_number}`}>
                              <PencilSimple size={16} />
                            </button>
                            <button onClick={() => deleteRappelTarget(target.client_number)} className="p-1.5 text-stone-400 hover:text-red-500 rounded" data-testid={`btn-delete-rappel-${target.client_number}`}>
                              <Trash size={16} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 'alerts':
        return (
          <div className="space-y-6">
            <p className="text-sm text-stone-500 dark:text-stone-400">
              Configure a sensibilidade das alertas automáticas mostradas no ícone de Campana.
            </p>

            <div className="space-y-2">
              <Label>Frequência de visita</Label>
              <p className="text-xs text-stone-400 mb-1">
                Cada cliente pode ter a sua própria frequência de visita.
              </p>
              <Button
                variant="outline"
                className="w-full"
                onClick={openClientAlertsDialog}
                data-testid="btn-configurar-alertas-por-cliente"
              >
                Configurar Alertas por Cliente
              </Button>
            </div>
          </div>
        );

      case 'password_access':
        return (
          <div className="space-y-6" data-testid="password-access-section">
            {/* Change real account password */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-stone-700 dark:text-stone-300 flex items-center gap-2">
                <Key size={16} className="text-cyan-600" /> Alterar Senha
              </h4>
              <p className="text-xs text-stone-400">
                É a senha da sua conta ({sessionUser?.email}), usada para entrar na aplicação.
              </p>
              <Input
                type="password"
                placeholder="Senha atual"
                value={changePwForm.current}
                onChange={(e) => setChangePwForm(prev => ({ ...prev, current: e.target.value }))}
                data-testid="input-current-password"
              />
              <Input
                type="password"
                placeholder="Nova senha"
                value={changePwForm.next}
                onChange={(e) => setChangePwForm(prev => ({ ...prev, next: e.target.value }))}
                data-testid="input-new-password"
              />
              <Input
                type="password"
                placeholder="Confirmar nova senha"
                value={changePwForm.confirm}
                onChange={(e) => setChangePwForm(prev => ({ ...prev, confirm: e.target.value }))}
                data-testid="input-confirm-new-password"
              />
              <Button
                className="w-full btn-primary"
                onClick={handleChangePassword}
                disabled={changingPassword}
                data-testid="btn-change-password"
              >
                {changingPassword ? 'A alterar...' : 'Alterar Senha'}
              </Button>
            </div>

            {/* Face ID / Touch ID passkeys tied to the real account */}
            <div className="space-y-3 pt-4 border-t border-stone-200 dark:border-stone-700">
              <h4 className="text-sm font-medium text-stone-700 dark:text-stone-300 flex items-center gap-2">
                <Fingerprint size={16} className="text-cyan-600" /> Face ID / Touch ID
              </h4>
              <p className="text-xs text-stone-400">
                Ative para entrar na aplicação com o Face ID/Touch ID deste dispositivo, sem escrever a senha.
              </p>

              {passkeyPlatformSupported ? (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={registerPasskey}
                  disabled={registeringPasskey}
                  data-testid="btn-register-passkey"
                >
                  <Fingerprint size={16} className="mr-2" />
                  {registeringPasskey ? 'A ativar...' : 'Ativar neste dispositivo'}
                </Button>
              ) : (
                <p className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 p-2 rounded-lg">
                  Este dispositivo/navegador não suporta Face ID / Touch ID.
                </p>
              )}

              {loadingPasskeys ? (
                <p className="text-sm text-stone-400">A carregar...</p>
              ) : passkeys.length > 0 && (
                <div className="space-y-2">
                  {passkeys.map((pk) => (
                    <div key={pk.id} className="flex items-center justify-between p-2.5 bg-stone-50 dark:bg-stone-800 rounded-lg" data-testid={`passkey-row-${pk.id}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <Laptop size={18} className="text-stone-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-stone-700 dark:text-stone-200 truncate">{pk.device_label}</p>
                          <p className="text-xs text-stone-400">{new Date(pk.created_at).toLocaleDateString('pt-PT')}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => removePasskey(pk.id)}
                        disabled={passkeyActionId === pk.id}
                        className="p-1.5 text-stone-400 hover:text-red-500 rounded flex-shrink-0"
                        data-testid={`btn-remove-passkey-${pk.id}`}
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 'users':
        return (
          <div className="space-y-4" data-testid="admin-users-section">
            <p className="text-sm text-stone-500 dark:text-stone-400">
              Crie novos acessos ou bloqueie/desbloqueie o acesso de qualquer utilizador à aplicação. Bloquear termina a sessão dele(a) imediatamente, mesmo que já esteja ligado(a).
            </p>

            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-stone-700 dark:text-stone-300">
                Utilizadores ({adminUsers.length})
              </h4>
              <Button size="sm" variant="outline" onClick={() => setShowAddUserForm(!showAddUserForm)} data-testid="btn-toggle-add-user">
                <Plus size={14} className="mr-1" /> Novo
              </Button>
            </div>

            {showAddUserForm && (
              <div className="p-3 rounded-lg bg-stone-50 dark:bg-stone-800 space-y-2" data-testid="add-user-form">
                <Input placeholder="Nome" value={newUserForm.name}
                  onChange={(e) => setNewUserForm(prev => ({ ...prev, name: e.target.value }))}
                  data-testid="input-new-user-name" />
                <Input placeholder="Email" type="email" value={newUserForm.email}
                  onChange={(e) => setNewUserForm(prev => ({ ...prev, email: e.target.value }))}
                  data-testid="input-new-user-email" />
                <Input placeholder="Senha (mín. 4 caracteres)" type="password" value={newUserForm.password}
                  onChange={(e) => setNewUserForm(prev => ({ ...prev, password: e.target.value }))}
                  data-testid="input-new-user-password" />
                <Select value={newUserForm.role} onValueChange={(val) => setNewUserForm(prev => ({ ...prev, role: val }))}>
                  <SelectTrigger data-testid="select-new-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Utilizador</SelectItem>
                    <SelectItem value="admin">Administrador</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" className="btn-primary w-full" onClick={createAdminUser} disabled={savingNewUser} data-testid="btn-save-new-user">
                  {savingNewUser ? 'A criar...' : 'Criar Acesso'}
                </Button>
              </div>
            )}

            {loadingAdminUsers ? (
              <p className="text-sm text-stone-400">A carregar...</p>
            ) : (
              <div className="space-y-2">
                {adminUsers.map((u) => (
                  <div key={u.id} className="p-3 rounded-lg bg-stone-50 dark:bg-stone-800 flex items-start justify-between gap-2" data-testid={`user-row-${u.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-stone-800 dark:text-stone-100 truncate">{u.name}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.is_active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'}`} data-testid={`user-status-${u.id}`}>
                          {u.is_active ? 'Ativo' : 'Bloqueado'}
                        </span>
                        {u.role === 'admin' && (
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">Admin</span>
                        )}
                      </div>
                      <p className="text-xs text-stone-400 truncate">{u.email}</p>
                    </div>
                    {u.id !== sessionUser?.id && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          className={u.is_active ? "text-amber-600 hover:text-amber-700 hover:bg-amber-50" : "text-green-600 hover:text-green-700 hover:bg-green-50"}
                          disabled={userActionId === u.id}
                          onClick={() => toggleBlockUser(u)}
                          data-testid={`btn-toggle-block-${u.id}`}
                        >
                          {u.is_active ? 'Bloquear' : 'Desbloquear'}
                        </Button>
                        <button
                          onClick={() => deleteAdminUser(u)}
                          disabled={userActionId === u.id}
                          className="p-1.5 text-stone-400 hover:text-red-500 rounded"
                          data-testid={`btn-delete-user-${u.id}`}
                          title="Eliminar utilizador"
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    )}
                    {u.id === sessionUser?.id && (
                      <span className="text-xs text-stone-400 italic flex-shrink-0 pt-1">a sua conta</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'storage':
        return (
          <div className="space-y-4" data-testid="admin-storage-section">
            <p className="text-sm text-stone-500 dark:text-stone-400">
              Tamanho real da base de dados (onde ficam guardadas todas as fotos de catálogos, ambientes, showroom e PDFs) e espaço em disco do servidor onde esta app está a correr.
            </p>
            {loadingStorageStats ? (
              <p className="text-sm text-stone-400">A carregar...</p>
            ) : storageStats ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="p-4 rounded-xl bg-teal-500/10 border border-teal-500/20" data-testid="storage-db-size-card">
                    <p className="text-xs text-stone-500 dark:text-stone-400">Base de dados (fotos + PDFs)</p>
                    <p className="text-2xl font-bold text-teal-600">{(storageStats.db_data_size_bytes / 1024 / 1024).toFixed(1)} MB</p>
                    <p className="text-xs text-stone-400 mt-1">{storageStats.files_count} ficheiro(s) guardados</p>
                  </div>
                  <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20" data-testid="storage-disk-free-card">
                    <p className="text-xs text-stone-500 dark:text-stone-400">Espaço livre no servidor</p>
                    <p className="text-2xl font-bold text-amber-600">{(storageStats.disk_free_bytes / 1024 / 1024 / 1024).toFixed(1)} GB</p>
                    <p className="text-xs text-stone-400 mt-1">de {(storageStats.disk_total_bytes / 1024 / 1024 / 1024).toFixed(1)} GB totais</p>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between text-xs text-stone-500 dark:text-stone-400 mb-1">
                    <span>Disco usado</span>
                    <span data-testid="storage-disk-percent">{((storageStats.disk_used_bytes / storageStats.disk_total_bytes) * 100).toFixed(1)}%</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-stone-200 dark:bg-stone-700 overflow-hidden" data-testid="storage-disk-bar">
                    <div
                      className="h-full bg-[#009392] rounded-full"
                      style={{ width: `${Math.min(100, (storageStats.disk_used_bytes / storageStats.disk_total_bytes) * 100)}%` }}
                    />
                  </div>
                </div>

                <p className="text-xs text-stone-400">
                  Nota: em produção, a base de dados corre num servidor próprio e separado deste (Preview) - estes números refletem sempre o ambiente onde estiveres a ver esta secção.
                </p>

                <Button size="sm" variant="outline" onClick={fetchStorageStats} disabled={loadingStorageStats} data-testid="btn-refresh-storage-stats">
                  Atualizar
                </Button>
              </>
            ) : (
              <p className="text-sm text-stone-400">Não foi possível carregar os dados de armazenamento.</p>
            )}
          </div>
        );

      default:
        return renderSettingsSectionContent();
    }
  };

  return (
    <div className="space-y-6 pb-24 md:pb-6 max-w-2xl lg:max-w-5xl mx-auto" data-testid="profile-page">
      {/* Header */}
      <PageHeader
        icon={User}
        title={activeSection ? settingsSections.find(s => s.id === activeSection)?.title : 'Configurações'}
        onBack={activeSection ? () => setActiveSection(null) : undefined}
        actions={!activeSection && (
          <Button
            onClick={handleLogout}
            variant="outline"
            className="text-stone-600 hover:text-red-600 hover:border-red-200 hover:bg-red-50"
            data-testid="btn-logout"
          >
            <SignOut size={18} className="mr-2" />
            Sair
          </Button>
        )}
      />

      {activeSection ? (
        /* Settings Section Content */
        <div className="gresart-card dark:bg-stone-900 dark:border-stone-700">
          {renderRappelAlertsContent()}
        </div>
      ) : (
        <>
          {/* Settings Section */}
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {settingsSections.filter(s => !s.hidden).map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className="gresart-card dark:bg-stone-900 dark:border-stone-700 hover:shadow-md transition-all flex items-center gap-4 text-left"
                  data-testid={`settings-btn-${section.id}`}
                >
                  <div className={`p-3 rounded-xl ${section.iconBg} ${section.iconColor}`}>
                    <section.icon size={24} weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-stone-900 dark:text-white">{section.title}</h3>
                    <p className="text-sm text-stone-500 dark:text-stone-400 truncate">{section.subtitle}</p>
                  </div>
                  <CaretRight size={20} className="text-stone-400 flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* PIN Dialog */}
      <Dialog open={showPinDialog} onOpenChange={setShowPinDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-[Manrope] text-center">
              {getPinDialogTitle()}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-6 space-y-6">
            {/* PIN Display */}
            <div className="flex justify-center gap-3">
              {[0, 1, 2, 3].map((i) => {
                const display = getCurrentPinDisplay();
                const filled = display.length > i;
                return (
                  <div
                    key={i}
                    className={`w-14 h-14 rounded-xl border-2 flex items-center justify-center text-2xl font-bold transition-all ${
                      filled 
                        ? 'border-[#00A19A] bg-[#00A19A]/10 text-[#00A19A]' 
                        : 'border-stone-300 dark:border-stone-600'
                    }`}
                  >
                    {filled ? (showPin ? display[i] : '•') : ''}
                  </div>
                );
              })}
            </div>

            {/* Show/Hide PIN toggle */}
            <button
              onClick={() => setShowPin(!showPin)}
              className="mx-auto flex items-center gap-2 text-sm text-stone-500 hover:text-stone-700"
            >
              {showPin ? <EyeSlash size={18} /> : <Eye size={18} />}
              {showPin ? 'Ocultar' : 'Mostrar'}
            </button>

            {/* Error message */}
            {pinError && (
              <p className="text-center text-red-500 text-sm font-medium">{pinError}</p>
            )}

            {/* Simple text input alternative */}
            <div className="text-center">
              <Label className="text-sm text-stone-500 dark:text-stone-400">
                {t('settings_pin_or_type') || 'Ou introduza diretamente:'}
              </Label>
              <Input
                type={showPin ? 'text' : 'password'}
                maxLength={4}
                value={getCurrentPinDisplay()}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                  if (pinAction === 'change' || pinAction === 'remove') {
                    if (pinStep === 1) setCurrentPin(val);
                    else if (pinStep === 2) setPinValue(val);
                    else if (pinStep === 3) setPinConfirm(val);
                  } else {
                    if (pinStep === 1) setPinValue(val);
                    else if (pinStep === 2) setPinConfirm(val);
                  }
                  setPinError('');
                }}
                className="mt-2 text-center text-2xl tracking-[0.5em] max-w-[160px] mx-auto"
                placeholder="••••"
                data-testid="pin-input-text"
              />
            </div>

            {/* Numeric Keypad */}
            <NumericKeypad onInput={handlePinInput} onBackspace={handlePinBackspace} />

            {/* Continue Button */}
            <Button
              className="w-full btn-primary"
              disabled={
                (pinAction === 'remove' && currentPin.length !== 4) ||
                (pinAction === 'set' && ((pinStep === 1 && pinValue.length !== 4) || (pinStep === 2 && pinConfirm.length !== 4))) ||
                (pinAction === 'change' && (
                  (pinStep === 1 && currentPin.length !== 4) ||
                  (pinStep === 2 && pinValue.length !== 4) ||
                  (pinStep === 3 && pinConfirm.length !== 4)
                ))
              }
              onClick={handlePinContinue}
              data-testid="btn-pin-continue"
            >
              {pinAction === 'remove' 
                ? (t('settings_remove_pin') || 'Remover PIN')
                : (pinStep === (pinAction === 'change' ? 3 : 2) ? (t('common_save') || 'Guardar') : (t('settings_pin_next') || 'Continuar'))
              }
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Per-client Alert Settings Dialog */}
      <Dialog open={showClientAlertsDialog} onOpenChange={setShowClientAlertsDialog}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col" data-testid="client-alerts-dialog">
          <DialogHeader>
            <DialogTitle>Configurar Alertas por Cliente</DialogTitle>
          </DialogHeader>
          <Input
            placeholder="Buscar cliente..."
            value={clientAlertSearch}
            onChange={(e) => setClientAlertSearch(e.target.value)}
            data-testid="input-search-client-alerts"
          />
          <div className="flex-1 overflow-y-auto space-y-2 pr-1">
            {loadingClientAlertSettings ? (
              <p className="text-sm text-stone-400 text-center py-4">A carregar clientes...</p>
            ) : filteredClientAlertSettings.length === 0 ? (
              <p className="text-sm text-stone-400 text-center py-4">Nenhum cliente encontrado.</p>
            ) : (
              filteredClientAlertSettings.map((c) => (
                <div key={c.client_id} className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-stone-50 dark:bg-stone-800" data-testid={`client-alert-row-${c.client_id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-stone-800 dark:text-stone-100 truncate">{c.client_name}</p>
                    {c.visit_frequency_days === 0 ? (
                      <p className="text-xs text-amber-600 font-medium">Inativo · sem obrigação de visita</p>
                    ) : !c.is_custom ? (
                      <p className="text-xs text-stone-400">Padrão</p>
                    ) : null}
                  </div>
                  <Select
                    value={String(c.visit_frequency_days)}
                    onValueChange={(val) => updateClientAlertFrequency(c.client_id, val)}
                    disabled={savingClientAlertId === c.client_id}
                  >
                    <SelectTrigger className="w-40 flex-shrink-0" data-testid={`select-client-alert-frequency-${c.client_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Inativo (sem alerta)</SelectItem>
                      <SelectItem value="30">30 dias</SelectItem>
                      <SelectItem value="45">45 dias</SelectItem>
                      <SelectItem value="60">60 dias</SelectItem>
                      <SelectItem value="90">90 dias</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))
            )}
          </div>
          <Button variant="outline" onClick={() => setShowClientAlertsDialog(false)} data-testid="btn-close-client-alerts-dialog">
            Fechar
          </Button>
        </DialogContent>
      </Dialog>

      {/* Price List Removal Confirmation Dialog */}
      <Dialog open={showPriceListRemovalDialog} onOpenChange={(open) => { if (!open) cancelPriceListRemoval(); }}>
        <DialogContent className="sm:max-w-lg" data-testid="price-list-removal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Warning size={22} className="text-amber-500" />
              Confirmar Remoção de Referências
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-stone-600 dark:text-stone-300">
              O novo ficheiro não inclui <strong>{priceListRemovalPreview?.to_remove?.length || 0}</strong> referência(s) que existem atualmente na Lista de Preços. Confirma a remoção destas referências?
            </p>
            <div className="max-h-56 overflow-y-auto space-y-1 bg-stone-50 dark:bg-stone-800 rounded-lg p-3" data-testid="price-list-removal-list">
              {(priceListRemovalPreview?.to_remove || []).map((p) => (
                <div key={p.product_code} className="text-sm flex justify-between gap-2">
                  <span className="text-stone-700 dark:text-stone-200 truncate">{p.product_name || "Sem nome"}</span>
                  <span className="text-stone-400 flex-shrink-0">{p.product_code}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-stone-400">
              {priceListRemovalPreview?.to_add_count || 0} referência(s) nova(s) serão adicionadas e {priceListRemovalPreview?.to_update_count || 0} serão atualizadas, independentemente desta escolha.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={cancelPriceListRemoval} data-testid="btn-cancel-price-list-removal">
              Cancelar
            </Button>
            <Button
              className="bg-red-500 hover:bg-red-600 text-white"
              onClick={confirmPriceListRemoval}
              disabled={uploadingPriceList}
              data-testid="btn-confirm-price-list-removal"
            >
              {uploadingPriceList ? 'A processar...' : 'Confirmar e Remover'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
